import { MultiSelect } from "react-multi-select-component";

import FormField from "components/FormField";
import Checkbox from "components/Checkbox";

import styles from "./TruckAvailabilitySection.module.scss";

// Read-only display helpers — keep rows resilient to missing backend values.
const formatValue = (value) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const formatAssociatedUsers = (names) =>
  Array.isArray(names) && names.length > 0 ? names.join(", ") : "—";

const formatDriverNames = (drivers) => {
  if (!Array.isArray(drivers) || drivers.length === 0) return "—";
  const names = drivers.map((d) => d?.name).filter(Boolean);
  return names.length > 0 ? names.join(", ") : "—";
};

const TruckAvailabilitySection = ({
  allActiveTrucks,
  selectedTruckIds,
  maxMilesOutAir,
  availableTrucks,
  availableTrucksLoading,
  availableTrucksError,
  maxMilesError,
  onChange,
}) => {
  const trucks = Array.isArray(availableTrucks) ? availableTrucks : [];
  const selectedIds = Array.isArray(selectedTruckIds) ? selectedTruckIds : [];

  const trucksById = trucks.reduce((acc, truck) => {
    acc[truck.id] = truck;
    return acc;
  }, {});

  const truckOptions = trucks.map((truck) => ({
    value: truck.id,
    label: String(truck.unit),
  }));

  const selectedOptions = selectedIds.map((id) => ({
    value: id,
    label: trucksById[id] ? String(trucksById[id].unit) : `Truck ${id}`,
  }));

  // Only specifically-selected trucks present in the active pool get a row;
  // a selected-but-now-inactive truck silently drops out (AC-20).
  const selectedTrucks = selectedIds
    .map((id) => trucksById[id])
    .filter((truck) => truck !== undefined && truck !== null);

  return (
    <div className={styles.truckAvailability}>
      <Checkbox
        id="all_active_trucks"
        label="All active trucks"
        value={allActiveTrucks}
        onChange={(e) => onChange("all_active_trucks", e.target.checked)}
      />

      {/* Shown whenever the section is visible — a pool-load failure must be
          surfaced even while "All active trucks" is ON (the multiselect is hidden). */}
      {availableTrucksError && (
        <div className={styles.fieldError}>{availableTrucksError}</div>
      )}

      {!allActiveTrucks && (
        <div className={styles.multiSelectField}>
          <div className={styles.fieldLabel}>
            Send RfQ only if Truck is Available and Matches the Load
            <span className={styles.helpWrapper}>
              <button
                type="button"
                className={styles.helpBadge}
                aria-label="How truck matching works"
              >
                ?
              </button>
              <span role="tooltip" className={styles.helpTooltip}>
                A selected truck matches a load only when all of these are true:
                <ul>
                  <li>
                    <strong>Available</strong> — the truck has a real available date/time.
                  </li>
                  <li>
                    <strong>Pickup not before availability</strong> — pickup is at or after
                    the truck's available time.
                  </li>
                  <li>
                    <strong>Within Max Miles Out (air)</strong> — straight-line distance from
                    the truck to the pickup.
                  </li>
                  <li>
                    <strong>Fits the load</strong> — load length, width, height and weight
                    are within the truck's limits.
                  </li>
                  <li>
                    <strong>Dock-high</strong> — load doesn't need dock-high, or the truck is
                    dock-high capable.
                  </li>
                  <li>
                    <strong>Cross-border</strong> — load is USA-only, or a driver can cross
                    the US–Canada border.
                  </li>
                  <li>
                    <strong>Hazmat</strong> — load isn't hazmat, or a driver is hazmat
                    certified.
                  </li>
                </ul>
                <p>If none of the selected trucks match a load, no RfQ is sent for it.</p>
              </span>
            </span>
          </div>
          <MultiSelect
            options={truckOptions}
            value={selectedOptions}
            onChange={(selected) =>
              onChange(
                "trucks",
                selected.map((item) => item.value),
              )
            }
            labelledBy="Select trucks"
            hasSelectAll={false}
            disabled={availableTrucksLoading}
            overrideStrings={{
              selectSomeItems: availableTrucksLoading
                ? "Loading trucks..."
                : "Select trucks (leave empty to disable the truck filter)",
            }}
          />
        </div>
      )}

      {!allActiveTrucks && selectedTrucks.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.headerRow}>
                <th className={styles.headerCell}>Truck #</th>
                <th className={styles.headerCell}>Associated User(s)</th>
                <th className={styles.headerCell}>Dispatcher</th>
                <th className={styles.headerCell}>Driver(s)</th>
                <th className={styles.headerCell} aria-label="Remove">
                  <span className={styles.srOnly}>Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedTrucks.map((truck) => (
                <tr key={truck.id} className={styles.dataRow}>
                  <td className={styles.dataCell}>
                    <span className={styles.truckUnit}>#{formatValue(truck.unit)}</span>
                  </td>
                  <td className={styles.dataCell}>
                    {formatAssociatedUsers(truck.associated_users_names)}
                  </td>
                  <td className={styles.dataCell}>
                    {formatValue(truck.dispatcher?.name)}
                  </td>
                  <td className={styles.dataCell}>
                    {formatDriverNames(truck.drivers)}
                  </td>
                  <td className={styles.dataCell}>
                    <button
                      type="button"
                      className={styles.removeButton}
                      aria-label={`Remove truck ${formatValue(truck.unit)}`}
                      onClick={() => {
                        const remainingIds = selectedIds.filter((id) => id !== truck.id);
                        onChange("trucks", remainingIds);
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.maxMilesField}>
        <div className={styles.fieldLabel}>Max Miles Out (air)</div>
        <FormField
          type="number"
          value={maxMilesOutAir}
          onChange={(e) => onChange("max_miles_out_air", e.target.value)}
          placeholderText="300"
          maxWidth="200px"
        />
        {maxMilesError && <div className={styles.fieldError}>{maxMilesError}</div>}
      </div>
    </div>
  );
};

export default TruckAvailabilitySection;
